/**
 * Ligar o segundo fator, passo a passo.
 *
 * **Quem está do outro lado desta tela é um pastor com o celular na mão**, provavelmente pela
 * primeira vez na vida fazendo isto. Então ela mostra **um passo por vez**, diz em que passo a
 * pessoa está, e cada passo tem uma frase e uma coisa para fazer. Uma tela só, com o quadrado,
 * o segredo em letras, a caixa do código e os dez códigos de emergência todos juntos, seria
 * verdadeira e inútil.
 *
 * Três decisões que vieram da condição que o Cristiano pôs ao aprovar o plano:
 *
 * 1. **O código de teste antes de valer.** Nada é ligado até a pessoa provar que o aplicativo
 *    dela mostra o número certo. Sem isso, alguém guardaria um segredo lido torto e descobriria
 *    no dia seguinte, já trancado do lado de fora.
 * 2. **O segredo em letras junto do quadrado.** Câmera de celular velho, tela com pouco brilho
 *    e dedo tremendo são comuns. Digitar é feio e sempre funciona.
 * 3. **Os códigos de emergência aparecem uma vez só**, e a tela diz isso antes de mostrá-los,
 *    e não depois.
 */
import { useEffect, useState, type JSX } from 'react'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type ComoEstaOSegundoFator, type Eu, type SegundoFatorComecado
} from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'

/** Os aplicativos que a gente sabe que funcionam, para ninguém escolher no escuro. */
const APLICATIVOS = ['Google Authenticator', 'Microsoft Authenticator', 'Authy', '2FAS']

type Onde =
  | { passo: 'vendo' }
  | { passo: 'aplicativo' }
  | { passo: 'quadrado'; comecada: SegundoFatorComecado }
  | { passo: 'teste'; comecada: SegundoFatorComecado }
  | { passo: 'guardar'; codigos: string[] }

export function SegundoFator({
  eu,
  aoSair,
  obrigada
}: {
  eu: Eu
  aoSair: () => void
  /**
   * A conta é nova e não vê mais nada enquanto não ligar. Muda duas coisas na tela: o caminho
   * de voltar some (não há para onde voltar) e a primeira tela explica por que ela está aqui.
   */
  obrigada?: boolean
}): JSX.Element {
  const [como, setComo] = useState<ComoEstaOSegundoFator | null>(null)
  const [onde, setOnde] = useState<Onde>({ passo: 'vendo' })
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  useEffect(() => {
    let vale = true
    servidor
      .segundoFator()
      .then((recebido) => {
        if (vale) setComo(recebido)
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })
    return () => {
      vale = false
    }
  }, [aoSair])

  async function comecar(): Promise<void> {
    setErro(null)
    setOcupado(true)
    try {
      setOnde({ passo: 'quadrado', comecada: await servidor.comecarOSegundoFator() })
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível começar.')
    } finally {
      setOcupado(false)
    }
  }

  async function confirmar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setOcupado(true)
    try {
      const feito = await servidor.confirmarOSegundoFator(codigo.trim())
      setCodigo('')
      setOnde({ passo: 'guardar', codigos: feito.codigos_de_emergencia })
      setComo({ ligado: true, codigos_que_sobram: feito.codigos_de_emergencia.length })
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível confirmar.')
      setCodigo('')
    } finally {
      setOcupado(false)
    }
  }

  const pronto = como !== null || erro !== null

  return (
    <>
      <BarraDeCima
        eu={eu}
        aoSair={aoSair}
        {...(obrigada === true ? {} : { voltarPara: { endereco: '/', texto: 'Início' } })}
      />

      <main className="folha" data-pronto={pronto ? (como !== null ? 'sim' : 'erro') : 'nao'}>
        <h1>Segundo fator</h1>

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {como === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {como !== null && onde.passo === 'vendo' && (
          <ComoEsta
            como={como}
            obrigada={obrigada === true}
            aoLigar={() => setOnde({ passo: 'aplicativo' })}
          />
        )}

        {onde.passo === 'aplicativo' && (
          <section className="passo">
            <p className="onde-estou">Passo 1 de 3</p>
            <h2>Instale um aplicativo no celular</h2>
            <p className="explicacao">
              Ele vai mostrar um número de seis algarismos que troca a cada trinta segundos. É
              esse número que o painel vai pedir toda vez que você entrar.
            </p>
            <ul className="aplicativos">
              {APLICATIVOS.map((nome) => <li key={nome}>{nome}</li>)}
            </ul>
            <p className="explicacao miudo">
              Qualquer um deles serve, e todos são gratuitos. Se o seu celular já tem um, use
              o que já está lá.
            </p>
            <button className="principal" type="button" onClick={comecar} disabled={ocupado}>
              {ocupado ? 'Preparando...' : 'Já tenho o aplicativo'}
            </button>
          </section>
        )}

        {onde.passo === 'quadrado' && (
          <section className="passo">
            <p className="onde-estou">Passo 2 de 3</p>
            <h2>Aponte a câmera do aplicativo para este quadrado</h2>

            <img className="quadrado" src={onde.comecada.quadrado} alt="" width={240} height={240} />

            <details className="digitar">
              <summary>A câmera não está lendo?</summary>
              <p className="explicacao">
                No aplicativo, escolha digitar o código à mão e copie estas letras:
              </p>
              <p className="segredo">{emPedacos(onde.comecada.segredo)}</p>
              <p className="explicacao miudo">
                Não tem a letra O nem o número 1: o que parece O é zero, o que parece 1 é a
                letra I.
              </p>
            </details>

            <button
              className="principal"
              type="button"
              onClick={() => setOnde({ passo: 'teste', comecada: onde.comecada })}
            >
              Pronto, já apareceu no celular
            </button>
          </section>
        )}

        {onde.passo === 'teste' && (
          <section className="passo">
            <p className="onde-estou">Passo 3 de 3</p>
            <h2>Digite o número que o aplicativo está mostrando</h2>
            <p className="explicacao">
              É só para conferir que deu certo. <strong>Até aqui nada mudou</strong>: o segundo
              fator só começa a valer quando este número for aceito.
            </p>

            <form onSubmit={confirmar} className="confirmar-codigo">
              <label>
                Código
                <input
                  type="text"
                  className="codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  spellCheck={false}
                  maxLength={6}
                  autoFocus
                  required
                />
              </label>
              <button className="principal" type="submit" disabled={ocupado}>
                {ocupado ? 'Conferindo...' : 'Conferir e ligar'}
              </button>
            </form>

            <button
              type="button"
              className="discreto"
              onClick={() => setOnde({ passo: 'quadrado', comecada: onde.comecada })}
            >
              Voltar para o quadrado
            </button>
          </section>
        )}

        {onde.passo === 'guardar' && (
          <CodigosDeEmergencia codigos={onde.codigos} primeiraVez={obrigada === true} />
        )}
      </main>
    </>
  )
}

function ComoEsta({
  como,
  aoLigar,
  obrigada
}: {
  como: ComoEstaOSegundoFator
  aoLigar: () => void
  obrigada?: boolean
}): JSX.Element {
  if (!como.ligado) {
    return (
      /*
       * Dentro de `.passo`, como as outras, e não solto: a largura do botão principal vive
       * nessa regra. No primeiro retrato esta tela saiu com o botão estreito e as seguintes
       * com ele inteiro, e a diferença não queria dizer nada.
       */
      <section className="passo">
        {/*
          UM cartão, e não dois.

          Na conta nova havia dois cartões dourados empilhados: um dizendo por que a pessoa
          estava ali, outro dizendo o que muda. Os dois diziam quase a mesma coisa com cara de
          aviso, e o retrato mostrou o peso. Quem chega aqui obrigado precisa de uma explicação,
          não de duas.
        */}
        <p className="cartao">
          <span className="titulo">
            {obrigada === true
              ? (como.ja_teve === true
                ? 'O segundo fator desta conta foi desligado'
                : 'Esta conta é nova, e começa por aqui')
              : 'O que muda quando você liga'}
          </span>
          <span className="detalhe">
            {obrigada === true && como.ja_teve === true
              ? 'Quem cuida do sistema desligou, a pedido, o segundo fator desta conta. Antes de ver '
                + 'qualquer coisa do Campo, cadastre o aplicativo do celular de novo. A sua senha não '
                + 'mudou, e os códigos de emergência antigos não valem mais.'
              : obrigada === true
              ? 'Antes de ver qualquer coisa do Campo, ligue o segundo fator. Depois dele, além '
                + 'da senha o painel vai pedir um número que só aparece no seu celular, e que '
                + 'troca a cada trinta segundos.'
              : 'Hoje, quem souber a sua senha entra no painel. Depois de ligar, além da senha o '
                + 'painel vai pedir um número que só aparece no seu celular, e que troca a cada '
                + 'trinta segundos.'}
          </span>
        </p>
        <p className="explicacao">
          Você faz isto uma vez. Leva três passos e precisa do celular na mão agora.
        </p>
        <button className="principal" type="button" onClick={aoLigar}>
          Ligar o segundo fator
        </button>
      </section>
    )
  }

  const sobram = como.codigos_que_sobram ?? 0

  return (
    <section className="passo">
      <p className="cartao">
        <span className="titulo">O segundo fator está ligado</span>
        <span className="detalhe">
          Toda vez que você entrar, o painel vai pedir o número do aplicativo depois da senha.
        </span>
      </p>

      <p className={sobram <= 2 ? 'explicacao atencao' : 'explicacao'}>
        {sobram === 0
          ? 'Não sobrou nenhum código de emergência. Se você perder o celular agora, só o administrador do Campo consegue abrir.'
          : `Sobram ${sobram} ${sobram === 1 ? 'código de emergência' : 'códigos de emergência'} dos dez.`}
      </p>

      <button className="principal" type="button" onClick={aoLigar}>
        Trocar de celular
      </button>
      <p className="explicacao miudo">
        Trocar de celular apaga o anterior e dá dez códigos de emergência novos. Os antigos
        param de valer.
      </p>
    </section>
  )
}

/**
 * Os dez códigos, mostrados uma vez só.
 *
 * O aviso vem ANTES da lista, e não embaixo dela. Quem lê de cima para baixo precisa saber que
 * aquilo não volta antes de decidir fechar a tela, e não depois.
 */
function CodigosDeEmergencia(
  { codigos, primeiraVez }: { codigos: string[]; primeiraVez?: boolean }
): JSX.Element {
  return (
    <section className="passo">
      <p className="recado feito">Pronto. O segundo fator está ligado.</p>

      <h2>Guarde estes dez códigos agora</h2>
      <p className="explicacao atencao">
        Eles aparecem <strong>uma vez só</strong>. Nem o servidor consegue mostrar de novo.
        Imprima ou escreva num papel e guarde fora do celular.
      </p>
      <p className="explicacao">
        Cada um serve <strong>uma vez</strong>, no lugar do número do aplicativo, no dia em que
        o celular sumir ou quebrar. Usar um código abre aquela entrada e mais nada: o segundo
        fator continua ligado, no mesmo celular.
      </p>
      <p className="explicacao">
        <strong>Quando cadastrar um celular novo:</strong> quando o celular sumir, quebrar ou for
        trocado. Entre com um código, abra esta tela e toque em "Trocar de celular".
      </p>

      <ol className="codigos-de-emergencia">
        {codigos.map((codigo) => <li key={codigo}>{codigo}</li>)}
      </ol>

      <button className="principal" type="button" onClick={() => window.print()}>
        Imprimir esta lista
      </button>

      <p className="explicacao miudo">
        Se você perder o celular e os dez códigos, o administrador máximo do Campo desliga o
        segundo fator da sua conta, e você liga outro.
      </p>

      {primeiraVez === true && (
        <button className="principal" type="button" onClick={() => window.location.assign('/')}>
          Guardei. Abrir o painel
        </button>
      )}
    </section>
  )
}

/** O segredo em pedaços de quatro, que é como alguém consegue digitar sem se perder. */
function emPedacos(segredo: string): string {
  return (segredo.match(/.{1,4}/g) ?? [segredo]).join(' ')
}
